import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { isApiError } from './api-error';

/** Validation error key the templates read via `hasError('serverError')`. */
const SERVER_ERROR_KEY = 'serverError';

/** The ApiError message to show inline, or `fallbackMessage` when there is none. */
export function apiErrorMessage(error: unknown, fallbackMessage: string): string {
  const message = isApiError(error) ? error.message.trim() : '';
  return message || fallbackMessage;
}

/** HTTP status of an ApiError (0 for a network failure), or null for anything else. */
export function apiErrorStatus(error: unknown): number | null {
  return isApiError(error) ? error.status : null;
}

/**
 * Attaches `ApiError.details` field errors to the matching controls of `form`
 * as `{ serverError: message }` and returns the general message to show inline.
 *
 * Detail keys are dotted paths (`site_address.city`, `evidence_refs.0`); a key
 * with no exact control falls back to its nearest ancestor control. When every
 * detail landed on a control, `fallbackMessage` is returned so the inline text
 * does not repeat the field messages.
 */
export function applyApiErrorToForm(form: AbstractControl, error: unknown, fallbackMessage: string): string {
  const details = isApiError(error) ? Object.entries(error.details ?? {}) : [];
  const applied = details.map(([field, messages]) => setServerError(form, field, messages));

  if (applied.length > 0 && applied.every(Boolean)) {
    return fallbackMessage;
  }

  return apiErrorMessage(error, fallbackMessage);
}

function setServerError(form: AbstractControl, field: string, messages: string[]): boolean {
  const control = findControl(form, field);
  if (control == null) {
    return false;
  }

  control.setErrors({
    ...(control.errors ?? {}),
    [SERVER_ERROR_KEY]: messages.join(' '),
  });
  control.markAsTouched();
  return true;
}

function findControl(form: AbstractControl, field: string): AbstractControl | null {
  const path = field.split('.');

  for (let length = path.length; length > 0; length--) {
    const control = form.get(path.slice(0, length));
    if (control != null) {
      return control;
    }
  }

  return null;
}

/** Remove only server errors so a corrected form can be retried. */
export function clearServerErrors(control: AbstractControl): void {
  if (control.hasError(SERVER_ERROR_KEY)) {
    const errors = { ...control.errors };
    delete errors[SERVER_ERROR_KEY];
    control.setErrors(Object.keys(errors).length ? errors : null);
  }
  if (control instanceof FormGroup || control instanceof FormArray) {
    Object.values(control.controls).forEach(clearServerErrors);
  }
}

/** Field messages for forms whose controls span several wizard steps. */
export function serverErrorMessages(control: AbstractControl): string[] {
  const own = control.getError(SERVER_ERROR_KEY);
  const children = control instanceof FormGroup || control instanceof FormArray
    ? Object.values(control.controls).flatMap(serverErrorMessages) : [];
  return [...new Set([...(typeof own === 'string' ? [own] : []), ...children])];
}
